import { type SaveVis } from '../debug/saveVis';
import { type SaveResult } from './saveFlows';
import { devBus } from '../debug/devBus';

// DM6 — one save model: commit-clear + save-all + per-aspect, with the ideal of
// SAVE-ON-COMMIT. The conductor edits many surfaces (soundscape clips/beds,
// hero placements, atmosphere shafts); before this, ⌘S saved ONLY the
// soundscape, hero/atmosphere edits were button-only, and there was no global
// "you have unsaved work" readout. This hub coordinates them:
//
//   • OWNED aspects (manifest, atmosphere) — the hub fully owns their dirty
//     state + a debounced auto-save (save-on-commit), so a finished gizmo drag /
//     retag reaches disk WITHOUT a separate button press. The per-aspect buttons
//     still work (they call flush(id)); they're now a manual flush of the same
//     state, not the only way to persist.
//   • DELEGATE aspects (soundscape) — the timeline keeps its own proven,
//     conflict-aware save machine; it just tells the hub when its dirtiness
//     changes (for the global readout) and registers its flush (for save-all).
//
// ⌘S and the new "save all" button both call saveAll(), which flushes anything
// outstanding across every aspect. The design invariant (devmode-hardening DM6):
// a commit is visibly committed, and the scene never silently diverges from
// disk. The If-Match / changed-on-disk conflict guard still gates every write
// (it lives in saveFlows, unchanged) — a refused write surfaces as 'conflict'
// and holds, exactly like the timeline's soundscape conflict.
//
// Save-on-commit also dissolves DM1's stale-undo-vs-disk trap: because every
// commit (including an undo, which re-commits the reverted state) auto-saves,
// the undo stack no longer needs to be severed at a save boundary — disk always
// reflects the live scene. So the old gizmoUndo.clear()-on-save is retired in
// favour of "undo is just another commit" (see app.ts).

export type SaveOutcome = SaveResult | boolean;

/** Normalize the two save-flow return shapes (SaveResult | boolean) to one. */
const normalize = (o: SaveOutcome): SaveResult => (o === true ? 'saved' : o === false ? 'failed' : o);

type FlushFn = (opts?: { force?: boolean }) => Promise<SaveOutcome>;

interface OwnedAspect {
  readonly id: string;
  readonly flush: FlushFn;
  dirty: boolean;
  saving: boolean;
  vis: SaveVis;
  conflictPending: boolean; // a freshness conflict awaits reload-or-force
  editGen: number;          // bumped on every markDirty — the in-flight race guard
  queued: boolean;          // a save arrived while one was in flight
  flashStamp: number;       // guards the transient "saved ✓"
  timer: ReturnType<typeof setTimeout> | null; // the debounce (save-on-commit)
  flashTimer: ReturnType<typeof setTimeout> | null;
}

interface DelegateAspect {
  readonly id: string;
  readonly isDirty: () => boolean;
  readonly flush: () => Promise<unknown>;
}

export interface SaveHub {
  /** Register an aspect the hub fully manages (dirty / vis / debounced auto-save). */
  registerOwned(id: string, flush: FlushFn): void;
  /** Register an aspect that owns its own save machine (the timeline's soundscape). */
  registerDelegate(id: string, d: { isDirty: () => boolean; flush: () => Promise<unknown> }): void;
  /** Mark an OWNED aspect dirty + (if auto-save is on) schedule its debounced save.
   *  This is the save-on-commit entry point: call it from every commit site. */
  markDirty(id: string): void;
  /** A DELEGATE aspect's dirtiness changed — refresh the global readout only. */
  noteDelegateChanged(): void;
  /** Flush ONE owned aspect now (a per-aspect button). Resolves its outcome so the
   *  caller can paint it (e.g. the inspector's flashSaveResult). No-op → 'saved'. */
  flush(id: string, opts?: { force?: boolean }): Promise<SaveResult>;
  /** Flush everything dirty across all aspects (⌘S / the save-all button). */
  saveAll(): Promise<void>;
  isDirty(): boolean;
  dirtyCount(): number;
  visFor(id: string): SaveVis;
  /** The aggregate state for the global readout + save-all button. */
  globalVis(): SaveVis;
  /** Toggle save-on-commit. Default ON. When off, edits still mark dirty (the
   *  readout lights) but only ⌘S / a button flushes — the explicit-commit mode. */
  setAutoSave(on: boolean): void;
  autoSaveOn(): boolean;
  onChange(cb: () => void): () => void;
  dispose(): void;
}

export function createSaveHub(opts?: { debounceMs?: number }): SaveHub {
  const DEBOUNCE_MS = opts?.debounceMs ?? 900;
  const SAVED_HOLD_MS = 1500;
  const FAILED_HOLD_MS = 2500;
  const owned = new Map<string, OwnedAspect>();
  const delegates = new Map<string, DelegateAspect>();
  const listeners = new Set<() => void>();
  let autoSave = true;

  const emit = (): void => { for (const cb of listeners) cb(); };

  const registerOwned = (id: string, flush: FlushFn): void => {
    owned.set(id, {
      id, flush, dirty: false, saving: false, vis: 'clean', conflictPending: false,
      editGen: 0, queued: false, flashStamp: 0, timer: null, flashTimer: null,
    });
  };
  const registerDelegate = (id: string, d: { isDirty: () => boolean; flush: () => Promise<unknown> }): void => {
    delegates.set(id, { id, isDirty: d.isDirty, flush: d.flush });
  };

  // The owned-aspect save machine — a faithful generalization of the timeline's
  // runSave (in-flight race via editGen, trailing re-save, conflict-hold). Returns
  // the primary outcome so a per-aspect button can paint it.
  const runFlush = async (a: OwnedAspect, explicitForce = false): Promise<SaveResult> => {
    if (a.saving) { a.queued = true; return a.vis === 'conflict' ? 'conflict' : 'saved'; }
    if (!a.dirty) return 'saved'; // nothing to write
    if (a.timer) { clearTimeout(a.timer); a.timer = null; }
    a.saving = true; a.queued = false; a.vis = 'saving'; emit();
    const genAtStart = a.editGen;
    const force = explicitForce || a.conflictPending;
    let result: SaveResult = 'failed';
    try { result = normalize(await a.flush(force ? { force: true } : undefined)); } catch { result = 'failed'; }
    a.saving = false;
    const editedDuringFlight = a.editGen !== genAtStart;
    if (result === 'saved') {
      a.conflictPending = false;
      if (editedDuringFlight) {
        // The bytes on disk don't include the mid-flight edit — stay dirty so a
        // trailing save persists it.
        a.dirty = true; a.vis = 'dirty';
      } else {
        a.dirty = false;
        const stamp = (a.flashStamp = nowMs());
        a.vis = 'saved';
        if (a.flashTimer) clearTimeout(a.flashTimer);
        a.flashTimer = setTimeout(() => {
          if (!a.dirty && a.flashStamp === stamp) { a.vis = 'clean'; emit(); }
        }, SAVED_HOLD_MS);
      }
    } else if (result === 'conflict') {
      // Refused — the file changed on disk. Hold the conflict (no auto-revert)
      // until the next deliberate save forces, or a reload re-bases.
      a.conflictPending = true; a.vis = 'conflict';
    } else {
      a.conflictPending = false; a.vis = 'failed';
      if (a.flashTimer) clearTimeout(a.flashTimer);
      a.flashTimer = setTimeout(() => { if (a.dirty) { a.vis = 'dirty'; emit(); } }, FAILED_HOLD_MS);
    }
    devBus.emit('save:result', { aspect: a.id, result }); // DM14 — narrate the save
    emit();
    // Trailing save: one coalesced re-run if an edit / queued flush arrived
    // mid-flight and we succeeded (not on conflict → would auto-force; not on
    // failure → would retry-loop).
    if (result === 'saved' && a.dirty && (editedDuringFlight || a.queued)) {
      a.queued = false;
      void runFlush(a);
    }
    return result;
  };

  const markDirty = (id: string): void => {
    const a = owned.get(id);
    if (!a) return;
    a.dirty = true; a.editGen++; a.flashStamp = 0;
    if (a.vis !== 'conflict') a.vis = 'dirty'; // a conflict stays visible until resolved
    emit();
    if (!autoSave) return;
    if (a.timer) clearTimeout(a.timer);
    a.timer = setTimeout(() => { a.timer = null; void runFlush(a); }, DEBOUNCE_MS);
  };

  const noteDelegateChanged = (): void => emit();

  const flush = (id: string, o?: { force?: boolean }): Promise<SaveResult> => {
    const a = owned.get(id);
    if (!a) return Promise.resolve('saved');
    // A manual per-aspect button writes the current state even when the hub
    // thinks the aspect is clean — that's its job ("save this now"). The write is
    // idempotent (the conflict guard re-checks disk), so this is harmless and
    // preserves the pre-DM6 button affordance. Auto-save + save-all only fire on
    // real dirt (they gate on a.dirty), so this can't cause spurious writes.
    if (!a.dirty && a.vis !== 'conflict') a.dirty = true;
    return runFlush(a, !!o?.force);
  };

  const saveAll = async (): Promise<void> => {
    // Flush owned aspects that have pending work, then the delegates. Owned
    // aspects with a held conflict force on this deliberate save-all (the user
    // chose to overwrite); a clean aspect is a no-op.
    const jobs: Array<Promise<unknown>> = [];
    for (const a of owned.values()) {
      if (a.dirty || a.conflictPending) jobs.push(runFlush(a, a.conflictPending));
    }
    for (const d of delegates.values()) {
      if (d.isDirty()) jobs.push(d.flush());
    }
    await Promise.all(jobs);
    emit();
  };

  const isDirty = (): boolean => {
    for (const a of owned.values()) if (a.dirty) return true;
    for (const d of delegates.values()) if (d.isDirty()) return true;
    return false;
  };
  const dirtyCount = (): number => {
    let n = 0;
    for (const a of owned.values()) if (a.dirty) n++;
    for (const d of delegates.values()) if (d.isDirty()) n++;
    return n;
  };
  const visFor = (id: string): SaveVis => owned.get(id)?.vis ?? 'clean';

  // Aggregate, worst-state-wins so the readout never hides a problem:
  // conflict > failed > saving > dirty > saved-flash > clean.
  const globalVis = (): SaveVis => {
    let anyFailed = false, anySaving = false, anySaved = false;
    for (const a of owned.values()) {
      if (a.vis === 'conflict') return 'conflict';
      if (a.vis === 'failed') anyFailed = true;
      else if (a.vis === 'saving') anySaving = true;
      else if (a.vis === 'saved') anySaved = true;
    }
    if (anyFailed) return 'failed';
    if (anySaving) return 'saving';
    if (isDirty()) return 'dirty';
    if (anySaved) return 'saved';
    return 'clean';
  };

  const setAutoSave = (on: boolean): void => {
    autoSave = on;
    if (on) { for (const a of owned.values()) if (a.dirty && !a.timer && !a.saving) markDirty(a.id); }
    emit();
  };
  const autoSaveOn = (): boolean => autoSave;

  const onChange = (cb: () => void): (() => void) => { listeners.add(cb); return () => listeners.delete(cb); };

  const dispose = (): void => {
    for (const a of owned.values()) { if (a.timer) clearTimeout(a.timer); if (a.flashTimer) clearTimeout(a.flashTimer); }
    owned.clear(); delegates.clear(); listeners.clear();
  };

  return {
    registerOwned, registerDelegate, markDirty, noteDelegateChanged, flush, saveAll,
    isDirty, dirtyCount, visFor, globalVis, setAutoSave, autoSaveOn, onChange, dispose,
  };
}

// performance.now() isolated so the module reads cleanly; it's the same clock
// the timeline + gizmoUndo use for recency.
const nowMs = (): number => performance.now();
